type MigrationFn<From, To> = (old: From) => To | Promise<To>;

type AsyncFunction = (...args: any[]) => Promise<any>;

export default class Migrator<
  Final extends { version?: number },
  Deps extends Record<string, AsyncFunction> = Record<string, never>,
> {
  public readonly version: number;
  private readonly orderedVersions: number[];
  private readonly asyncDependencies = new Map<string, AsyncFunction>();

  constructor(
    private readonly migrations: Record<number, MigrationFn<any, any>>,
  ) {
    // Sort versions from supplied migrations
    this.orderedVersions = Object.keys(migrations)
      .map((k) => Number(k))
      .sort((a, b) => a - b);

    // Calculate current version
    this.version =
      this.orderedVersions.length > 0
        ? this.orderedVersions[this.orderedVersions.length - 1]
        : 0;
  }

  public registerAsyncDependency = <K extends string & keyof Deps>(
    key: K,
    fn: Deps[K],
  ): void => {
    this.asyncDependencies.set(key, fn);
  };

  public requireAsyncDependency = <K extends string & keyof Deps>(
    key: K,
  ): Deps[K] => {
    const dep = this.asyncDependencies.get(key);
    if (!dep) throw new Error('Missing async dependency: ' + key);
    return dep as Deps[K];
  };

  public migrate = async (raw: any): Promise<Final> => {
    const initialVersion = raw.version ?? 0;

    // If we somehow see a future version, don't touch it.
    if (initialVersion > this.version) {
      return raw as Final;
    }

    let currentVersion = initialVersion;
    let value: any = { ...raw };

    // Apply migrations
    for (const v of this.orderedVersions) {
      if (v > currentVersion) {
        value = await this.migrations[v](value);
        value.version = v;
        currentVersion = v;
      }
    }

    // Ensure we always end with the correct version set
    if (value.version !== this.version) {
      value.version = this.version;
    }

    return value as Final;
  };
}
